// @section 1 CRM route — tabbed list view
import { useEffect, useState } from 'react';
import { listCompanies, listContacts, listDeals, createCompany, createContact, type Company, type Contact, type Deal } from '../api';
import { useIsMobile } from '../lib/useIsMobile';

type Tab = 'companies' | 'contacts' | 'deals';

export default function Crm() {
  const [tab, setTab] = useState<Tab>('contacts');
  // @block 1.1 Mobile: own the scroll. main.v5-canvas is overflow:hidden, so a
  // plain page root would clip a long contacts/companies list on a phone.
  const isMobile = useIsMobile();

  return (
    <div style={isMobile ? { height: '100%', overflowY: 'auto', WebkitOverflowScrolling: 'touch', padding: '16px 14px' } : undefined}>
      <h1>CRM</h1>
      <div className="tabs">
        <button className={tab === 'contacts' ? 'tab active' : 'tab'} onClick={() => setTab('contacts')}>Contacts</button>
        <button className={tab === 'companies' ? 'tab active' : 'tab'} onClick={() => setTab('companies')}>Companies</button>
        <button className={tab === 'deals' ? 'tab active' : 'tab'} onClick={() => setTab('deals')}>Deals</button>
      </div>
      {tab === 'contacts' && <Contacts />}
      {tab === 'companies' && <Companies />}
      {tab === 'deals' && <Deals />}
    </div>
  );
}

function Companies() {
  const [items, setItems] = useState<Company[] | null>(null);
  const [q, setQ] = useState('');
  const [name, setName] = useState('');
  const [domain, setDomain] = useState('');
  const reload = () => listCompanies(q || undefined).then(setItems).catch(() => setItems([]));
  useEffect(() => { reload(); }, [q]);

  return (
    <div>
      <div className="row">
        <input placeholder="Search…" value={q} onChange={(e) => setQ(e.target.value)} />
      </div>
      <form className="row" onSubmit={async (e) => { e.preventDefault(); if (!name) return; await createCompany({ name, domain: domain || undefined }); setName(''); setDomain(''); reload(); }}>
        <input placeholder="Name" value={name} onChange={(e) => setName(e.target.value)} />
        <input placeholder="domain.com" value={domain} onChange={(e) => setDomain(e.target.value)} />
        <button type="submit">Add</button>
      </form>
      {items === null ? <em>Loading…</em> : items.length === 0 ? <em>No companies yet.</em> : (
        <table className="data">
          <thead><tr><th>Name</th><th>Domain</th><th>Industry</th></tr></thead>
          <tbody>{items.map((c) => <tr key={c.id}><td>{c.name}</td><td>{c.domain ?? '—'}</td><td>{c.industry ?? '—'}</td></tr>)}</tbody>
        </table>
      )}
    </div>
  );
}

function Contacts() {
  const [items, setItems] = useState<Contact[] | null>(null);
  const [q, setQ] = useState('');
  const [first, setFirst] = useState('');
  const [last, setLast] = useState('');
  const [email, setEmail] = useState('');
  const reload = () => listContacts(q || undefined).then(setItems).catch(() => setItems([]));
  useEffect(() => { reload(); }, [q]);

  return (
    <div>
      <div className="row"><input placeholder="Search contacts…" value={q} onChange={(e) => setQ(e.target.value)} /></div>
      <form className="row" onSubmit={async (e) => { e.preventDefault(); await createContact({ firstName: first, lastName: last, email }); setFirst(''); setLast(''); setEmail(''); reload(); }}>
        <input placeholder="First" value={first} onChange={(e) => setFirst(e.target.value)} />
        <input placeholder="Last" value={last} onChange={(e) => setLast(e.target.value)} />
        <input placeholder="email@…" value={email} onChange={(e) => setEmail(e.target.value)} />
        <button type="submit">Add</button>
      </form>
      {items === null ? <em>Loading…</em> : items.length === 0 ? <em>No contacts yet.</em> : (
        <table className="data">
          <thead><tr><th>Name</th><th>Email</th><th>Title</th></tr></thead>
          <tbody>{items.map((c) => <tr key={c.id}><td>{c.fullName ?? `${c.firstName ?? ''} ${c.lastName ?? ''}`}</td><td>{c.email ?? '—'}</td><td>{c.title ?? '—'}</td></tr>)}</tbody>
        </table>
      )}
    </div>
  );
}

function Deals() {
  const [items, setItems] = useState<Deal[] | null>(null);
  const [stage, setStage] = useState('');
  useEffect(() => { listDeals(stage || undefined).then(setItems).catch(() => setItems([])); }, [stage]);

  const stages = ['lead', 'qualified', 'proposal', 'negotiation', 'won', 'lost'];

  return (
    <div>
      <div className="row">
        <select value={stage} onChange={(e) => setStage(e.target.value)}>
          <option value="">All stages</option>
          {stages.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>
      {items === null ? <em>Loading…</em> : items.length === 0 ? <em>No deals yet.</em> : (
        <table className="data">
          <thead><tr><th>Name</th><th>Stage</th><th>Value</th><th>Close</th></tr></thead>
          <tbody>{items.map((d) => (
            <tr key={d.id}>
              <td>{d.name}</td>
              <td>{d.stage}</td>
              <td>{d.valueCents != null ? `$${(d.valueCents / 100).toLocaleString()}` : '—'}</td>
              <td>{d.expectedClose ?? '—'}</td>
            </tr>
          ))}</tbody>
        </table>
      )}
    </div>
  );
}
